import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { assoc } from 'ramda';
import qs from 'qs';
import { useReactToPrint } from 'react-to-print';

import { Paper, Grid, Typography, IconButton } from '@material-ui/core';
import { Pagination } from '@material-ui/lab';
import { Search, Close, FilterList } from '@material-ui/icons'
import { useFormik } from 'formik';
import Select from '../../../components/Select';
import MTable from '../../../components/MTable';
import TableMobile from '../../../components/TableMobile';
import Input from '../../../components/Input';
import Button from '../../../components/Button';
import ModalConfirmation from '../../../components/ModalConfirmation';
import ServicesRegistration from '../../../components/ServicesRegistration';
import Dropdown from '../../../components/Dropdown';
import ExportCSV from '../../../components/ExportCSV';

import ServicesExport from './Export';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMinusCircle, faEye, faEyeSlash, faPlusCircle } from '@fortawesome/free-solid-svg-icons';

import { loadServices, loadSingleService, loadServicesCategories, removeServices, changeVisibility, loadServicesExport } from '../store/thunk';
import { clearServicesSelected, selectSingleService, toggleRemoveModal, selectServices, clearForm } from '../store/actions';
import { persistQueryParamsSearch } from 'components/Filter/store/actions';

import { formatServicesCategories } from '../../../helpers/formatters';

import useWindowDimensions from 'helpers/hooks/useWindowDimensions';
import useDidMount from 'helpers/hooks/useDidMount';

const DownloadIcon = require('../../../assets/img/icon_download.svg');
const PencilIcon = require('../../../assets/img/icon_pencil.svg');

const headers = [
  { title: 'Categoria do serviço', field: 'categoriesLabel' },
  { title: 'Nome do serviço', field: 'descriptionLabel' },
  { title: 'Preço', field: 'amountLabel' },
  { title: 'Situação', field: 'statusLabel' }
];

const exportHeaders = [
  { id: 'categories', label: 'Categoria do serviço' },
  { id: 'description', label: 'Nome do serviço' },
  { id: 'amount', label: 'Preço' },
  { id: 'status', label: 'Situação' }
];

const width = window.innerWidth;
const tableRowWidth = width < 768 ? 120 : 100;

export default function Services() {
  const dispatch = useDispatch();

  const { width } = useWindowDimensions();
  const isSm = width < 960;

  const didComponentMount = useDidMount();

  const { userId } = useSelector(state => state.profile);
  const { establishmentId } = useSelector(state => state.businessInfo);
  const { qp, isExporting } = useSelector(state => state.filters);

  const {
    list,
    total,
    serviceId,
    serviceIds,
    servicesCategories,
    isLoading,
    isRemoving
  } = useSelector(state => state.services);

  const [servicesPage, setServicesPage] = useState(1);
  const servicesPageSize = 10;
  const servicesPageCount = Math.ceil(total / servicesPageSize);

  const [isServicesRegistrationModalOpen, setIsServicesRegistrationModalOpen] = useState(false);
  const [isFilterActive, setIsFilterActive] = useState(false);
  const [isFilterVisible, setIsFilterVisible] = useState(!isSm);

  const [exportData, setExportData] = useState([]);

  const contentRef = useRef(null);
  const reactToPrint = useReactToPrint({ contentRef, documentTitle: 'servicos', preserveAfterPrint: true });

  const disableRemoveButton = serviceIds.length > 0 && serviceIds.length < 2
    ? !serviceIds.includes(serviceId)
    : serviceIds.length > 1;

  const handleLoadServices = ({ page, qp }) => {
    const params = {
      userId,
      establishmentId,
      page,
      qp
    }

    dispatch(persistQueryParamsSearch(qp));
    dispatch(loadServices(params));
  }

  const handleRemoveSingleService = row => {
    const { serviceId } = row;

    dispatch(selectSingleService(serviceId));
    dispatch(toggleRemoveModal(!isRemoving));
  }

  const handleServiceEdit = (userId, establishmentId, service) => {
    const { serviceId } = service;
    dispatch(loadSingleService(userId, establishmentId, serviceId));
    setIsServicesRegistrationModalOpen(true);
  }

  useEffect(() => {
    if(userId) {
      dispatch(loadServicesCategories(userId));
    }
  }, [userId]);

  useEffect(() => {
    if(establishmentId) {
      handleLoadServices({ page: 1, qp: '' });
    }
  }, [establishmentId]);

  useEffect(() => {
    if(establishmentId && !didComponentMount) {
      handleLoadServices({ page: servicesPage, qp });
    }
  }, [servicesPage]);

  useEffect(() => {
    formik.resetForm();
    setIsFilterActive(false);
  }, [establishmentId]);

  const handleSubmitFilters = values => {
    const formattedParams = Object.fromEntries(Object.entries(values).filter(([_, value]) => value));
    const params = `&${qs.stringify(formattedParams)}`;

    setIsFilterActive(true);

    return handleLoadServices({ page: servicesPage, qp: params });
  }

  const handleRemoveModal = () => {
    dispatch(toggleRemoveModal(!isRemoving));
  }

  const handleRemoveServices = () => {
    dispatch(removeServices(userId, establishmentId, serviceIds)).then(() => {
      handleLoadServices({ page: servicesPage, qp });
    });
  }

  const handleSelectAllRows = rows => {
    dispatch(clearServicesSelected());

    rows.forEach(row => {
      const { serviceId } = row;
      dispatch(selectServices(serviceId, row.tableData.checked));
    });
  }

  const getFontColor = text => {
    return(
      <Typography color='textSecondary' variant='body2'>{text}</Typography>
    );
  }

  const formatServicesStatus = status => {
    if(+status == 1) {
      return(
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 5, height: 5, borderRadius: '50%', backgroundColor: '#43A047' }} />
          {getFontColor('Ativo')}
        </div>
      );
    }

    return(
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 10 }}>
        <div style={{ width: 5, height: 5, borderRadius: '50%', backgroundColor: '#F66C6B' }} />
        {getFontColor('Inativo')}
      </div>
    );
  }

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      status: '',
      serviceCategoryId: '',
      search: ''
    },
    onSubmit: values => handleSubmitFilters(values)
  });

  return(
    <Grid container spacing={3}>
      <div style={{ display: 'none' }}>
        <ServicesExport
          ref={contentRef}
          data={exportData}
        />
      </div>
      {isServicesRegistrationModalOpen && (
        <ServicesRegistration
          open={isServicesRegistrationModalOpen}
          onClose={() => setIsServicesRegistrationModalOpen(false)}
          servicesPage={servicesPage}
          servicesPageSize={servicesPageSize}
          qp={qp}
        />
      )}
      <Grid item xs={12}>
        <Button
          fullWidth={isSm}
          type='button'
          color='success'
          onClick={() => {
            dispatch(clearForm());
            setIsServicesRegistrationModalOpen(true);
          }}
          startIcon={
            <FontAwesomeIcon
              icon={faPlusCircle}
              color="#FFFFFF"
              size="xs"
            />
          }
        >
          Cadastrar Serviço
        </Button>
      </Grid>
      <Grid item xs={12}>
        <Paper>
          <div style={{ padding: 10 }}>
            {isSm && (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', backgroundColor: '#022A5C', padding: 10 }}>
                <Typography style={{ color: '#FFFFFF' }}>
                  <b>Serviços</b>
                </Typography>
              </div>
            )}
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <div style={{ width: '100%', display: 'flex', flexWrap: isSm ? 'wrap' : 'nowrap', justifyContent: 'center', gap: 5, marginTop: 10 }}>
                {isFilterVisible && (
                  <>
                    <div style={{ width: isSm ? '100%' : '45%' }}>
                      <Select
                        name='serviceCategoryId'
                        label='Cat. de serviço'
                        options={servicesCategories}
                        value={formik.values.serviceCategoryId}
                        onChange={formik.handleChange}
                      />
                    </div>
                    <div style={{ width: isSm ? '100%' : '45%' }}>
                      <Select
                        name='status'
                        label='Situação'
                        options={[
                          { label: 'Ativo', value: '1' },
                          { label: 'Inativo', value: '0' }
                        ]}
                        value={formik.values.status}
                        onChange={formik.handleChange}
                      />
                    </div>
                    {isSm && (
                      <div style={{ width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 10 }}>
                        <Button
                          fullWidth
                          type='button'
                          color='primary'
                          onClick={() => formik.submitForm()}
                        >
                          Filtrar
                        </Button>
                        {isFilterActive && (
                          <IconButton
                            type='button'
                            onClick={() => {
                              setIsFilterActive(false);
                              formik.resetForm();
                              dispatch(persistQueryParamsSearch(''));
                              return handleLoadServices({ page: servicesPage, qp: '' });
                            }}
                          >
                            <Close />
                          </IconButton>
                        )}
                      </div>
                    )}
                  </>
                )}
                <div style={{ width: isSm ? '100%' : '10%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                  {isSm && (
                    <Button
                      fullWidth
                      type='button'
                      color='primary'
                      startIcon={
                        <FilterList />
                      }
                      onClick={() => setIsFilterVisible(!isFilterVisible)}
                    >
                      Mais Filtros
                    </Button>
                  )}
                  {!isSm && (
                    <>
                      <Button
                        fullWidth
                        type='button'
                        color='primary'
                        onClick={() => formik.submitForm()}
                      >
                        Filtrar
                      </Button>
                      {isFilterActive && (
                        <IconButton
                          type='button'
                          onClick={() => {
                            setIsFilterActive(false);
                            formik.resetForm();
                            dispatch(persistQueryParamsSearch(''));
                            return handleLoadServices({ page: servicesPage, qp: '' });
                          }}
                        >
                          <Close />
                        </IconButton>
                      )}
                    </>
                  )}
                </div>
              </div>
              <div style={{ width: '100%', display: 'flex', justifyContent: 'flex-end' }}>
                <div style={{ width: isSm ? '100%' : '35%', display: 'flex', gap: 10, padding: 10 }}>
                  <Input
                    name='search'
                    label='Pesquisar'
                    value={formik.values.search}
                    onChange={formik.handleChange}
                    endAdornment={
                      <IconButton type='button' onClick={() => formik.submitForm()}>
                        <Search />
                      </IconButton>
                    }
                  />
                  <div style={{ marginTop: 10, marginLeft: 5 }}>
                    <Dropdown
                      label='Exportar'
                      color='tertiary'
                      variant='outlined'
                      icon={
                        <img
                          src={DownloadIcon}
                          height={18}
                          width={18}
                        />
                      }
                    >
                      <ExportCSV
                        requestData={() => dispatch(loadServicesExport({ userId, establishmentId, page: servicesPage, qp }))}
                        exportHeaders={exportHeaders}
                        exportType="servicesExport"
                        fileName="Serviços"
                      />
                      <Button
                        disableElevation
                        fullWidth
                        type='button'
                        color='inherit'
                        loading={isExporting}
                        onClick={() => {
                          dispatch(loadServicesExport({ userId, establishmentId, page: servicesPage, qp }))
                            .then(data => {
                              setExportData(data);
                              reactToPrint();
                            })
                            .catch(() => setExportData([]));
                        }}
                      >
                        PDF
                      </Button>
                    </Dropdown>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {isSm && (
            <TableMobile
              loading={isLoading}
              data={list?.map(service => {
                return {
                  ...service,
                  descriptionLabel: {
                    index: 1,
                    label: 'Nome do serviço',
                    value: getFontColor(service?.description)
                  },
                  categoriesLabel: {
                    index: 2,
                    label: 'Categoria do serviço',
                    value: getFontColor(formatServicesCategories(service?.categories, servicesCategories))
                  },
                  amountLabel: {
                    index: 3,
                    label: 'Preço',
                    value: getFontColor(service?.amount)
                  }
                }
              })}
              actions={[
                // {
                //   action: rowData => (
                //     <IconButton
                //       disabled={disableRemoveButton}
                //       onClick={() => handleRemoveSingleService(rowData)}
                //     >
                //       <FontAwesomeIcon
                //         icon={faMinusCircle}
                //         color="#F66D6E"
                //         size="xs"
                //         style={{ opacity: disableRemoveButton ? 0.5 : 1 }}
                //       />
                //     </IconButton>
                //   )
                // },
                {
                  action: rowData => (
                    <IconButton onClick={() => handleServiceEdit(userId, establishmentId, assoc('isUpdating', true, rowData))}>
                      <img
                        src={PencilIcon}
                        height={18}
                        width={18}
                      />
                    </IconButton>
                  )
                },
                {
                  action: rowData => {
                    const params = {
                      page: servicesPage,
                      pageSize: servicesPageSize,
                      qp
                    }

                    return(
                      <IconButton onClick={() => dispatch(changeVisibility(userId, establishmentId, rowData?.serviceId, params))}>
                        <FontAwesomeIcon
                          icon={rowData?.status ? faEye : faEyeSlash}
                          color="#022A5C"
                          size="xs"
                        />
                      </IconButton>
                    );
                  }
                }
              ]}
            />
          )}
          {!isSm && (
            <MTable
              loading={isLoading}
              headers={headers}
              data={list?.map(service => {
                return {
                  ...service,
                  categoriesLabel: getFontColor(formatServicesCategories(service?.categories, servicesCategories)),
                  descriptionLabel: getFontColor(service?.description),
                  amountLabel: getFontColor(service?.amount),
                  statusLabel: formatServicesStatus(service?.status)
                }
              })}
              actionColumnWidth={tableRowWidth}
              onSelectionChange={handleSelectAllRows}
              rowStyle={{ height: "75px" }}
              actions={[
                // rowData => ({
                //   icon: () => (
                //     <FontAwesomeIcon
                //       icon={faMinusCircle}
                //       color="#F66D6E"
                //       size="xs"
                //       style={{ opacity: disableRemoveButton ? 0.5 : 1 }}
                //     />
                //   ),
                //   onClick: () => handleRemoveSingleService(rowData),
                //   disabled: disableRemoveButton
                // }),
                rowData => ({
                  icon: () => (
                    <img
                      src={PencilIcon}
                      height={18}
                      width={18}
                    />
                  ),
                  onClick: () => handleServiceEdit(userId, establishmentId, assoc('isUpdating', true, rowData))
                }),
                rowData => ({
                  icon: () => (
                    <FontAwesomeIcon
                      icon={rowData?.status ? faEye : faEyeSlash}
                      color="#022A5C"
                      size="xs"
                    />
                  ),
                  onClick: () => {
                    const params = {
                      page: servicesPage,
                      pageSize: servicesPageSize,
                      qp
                    }

                    dispatch(changeVisibility(userId, establishmentId, rowData?.serviceId, params));
                  }
                })
              ]}
            />
          )}
          <Grid
            container
            xs={12}
            style={{ padding: 10, display: 'flex', justifyContent: 'flex-end' }}
          >
            <Pagination
              color="primary"
              variant="outlined"
              shape="rounded"
              count={servicesPageCount}
              page={servicesPage}
              onChange={(_, page) => setServicesPage(page)}
            />
          </Grid>
        </Paper>
      </Grid>
      <ModalConfirmation
        open={isRemoving}
        isLoading={isLoading}
        title='Confirmar remoção'
        onClose={handleRemoveModal}
        handleConfirmation={handleRemoveServices}
      >
        <Typography color='textSecondary'>
          Deseja excluir o serviço e todos os valores vinculados as categorias de veículos?
        </Typography>
      </ModalConfirmation>
    </Grid>
  );
}